/**
 * Antenna timeline: one row per antenna (grouped by station group), allocated
 * passes as blocks on the analysis window, GEO contacts as faint background
 * bands, conflicts as markers, the clock as a moving line. Clicking the axis
 * scrubs the shared clock so the globe follows; the axis is also a slider
 * (arrow keys step the clock by one tick). Row heights and the table font
 * follow the density setting.
 *
 * Keyboard: each row's antenna name is one tab stop; ArrowRight from it walks
 * into that row's passes (ArrowLeft and ArrowRight between them, Home back to
 * the name), so thousands of pass blocks are not each a tab stop.
 */
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { TriangleAlert } from 'lucide-react';
import { clockNow, useClock, useUI } from '@/state/store';
import { cn } from '@/lib/cn';
import { useLoader } from '../../store';
import type { Antenna, Mission } from '../../types';
import { fmtMinutes, fmtUtc, fmtUtcShort, pct, tierColor } from '../format';

const GROUP_ORDER = ['WCDAS', 'FCDAS', 'NSOF', 'CBU'];
const MAX_BARS = 50_000;

interface Block {
  key: string;
  missionId: string;
  startMs: number;
  endMs: number;
  durationMin: number;
  maxElevation: number;
  background: boolean;
  color: string;
}

function isGeo(m: Mission | undefined): boolean {
  if (!m) return false;
  return String(m.orbit.type).toUpperCase() === 'GEO' || (m.orbit.periodMinutes ?? 0) > 1400;
}

function tickStepMs(hours: number): number {
  if (hours <= 6) return 30 * 60_000;
  if (hours <= 24) return 2 * 3_600_000;
  if (hours <= 168) return 24 * 3_600_000;
  if (hours <= 720) return 7 * 24 * 3_600_000;
  return 30 * 24 * 3_600_000;
}

/** Arrow keys inside a row: the antenna button and its pass blocks form one roving group. */
function rowKeys(e: KeyboardEvent<HTMLDivElement>) {
  if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
  const items = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-row-item]')];
  const i = items.indexOf(document.activeElement as HTMLElement);
  if (i === -1) return;
  const next =
    e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : e.key === 'ArrowRight' ? Math.min(items.length - 1, i + 1) : Math.max(0, i - 1);
  e.preventDefault();
  items[next]?.focus();
}

function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(clockNow());
  useEffect(() => {
    const id = window.setInterval(() => setNow(clockNow()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function GanttPanel() {
  const dataset = useLoader((s) => s.dataset);
  const results = useLoader((s) => s.results);
  const timeframe = useLoader((s) => s.timeframe);
  const activeAntennaIds = useLoader((s) => s.activeAntennaIds);
  const visibleAntennaIds = useLoader((s) => s.visibleAntennaIds);
  const selection = useUI((s) => s.selection);
  const select = useUI((s) => s.select);
  const setSimTime = useClock((s) => s.setSimTime);
  const now = useNow();
  const [hover, setHover] = useState<{ block: Block; antennaId: string; x: number; y: number } | null>(null);

  const span = timeframe.endMs - timeframe.startMs;
  const xOf = (ms: number) => ((ms - timeframe.startMs) / span) * 100;

  const rows = useMemo(() => {
    if (!dataset || !results) return [];
    const active = new Set(activeAntennaIds);
    const visible = visibleAntennaIds ? new Set(visibleAntennaIds) : null;
    const missions = new Map(dataset.missions.map((m) => [m.id, m]));
    const tdrs = new Set(dataset.tdrss.map((t) => t.id));
    const byAntenna = new Map<string, Block[]>();
    let bars = 0;
    for (const [missionId, byAnt] of Object.entries(results.allocations)) {
      const mission = missions.get(missionId);
      for (const [antennaId, list] of Object.entries(byAnt)) {
        if (tdrs.has(antennaId)) continue;
        for (const w of list) {
          if (bars++ > MAX_BARS) break;
          const width = ((w.endMs - w.startMs) / span) * 100;
          if (!byAntenna.has(antennaId)) byAntenna.set(antennaId, []);
          byAntenna.get(antennaId)!.push({
            key: `${missionId}-${w.startMs}`,
            missionId,
            startMs: w.startMs,
            endMs: w.endMs,
            durationMin: w.durationMin,
            maxElevation: w.maxElevation,
            background: isGeo(mission) || width > 50,
            color: mission?.color ?? tierColor(dataset.tiers, mission?.priorityTier ?? 99),
          });
        }
      }
    }
    const conflictsByAntenna = new Map<string, { ms: number; title: string }[]>();
    for (const c of results.conflicts) {
      if (!conflictsByAntenna.has(c.antennaId)) conflictsByAntenna.set(c.antennaId, []);
      conflictsByAntenna.get(c.antennaId)!.push({
        ms: c.overlapStartMs,
        title: `${c.missionB} blocked by ${c.missionA}, ${c.blockedMinutes} min`,
      });
    }
    const antennas = dataset.antennas.filter((a) => active.has(a.id) && (!visible || visible.has(a.id)));
    const groups = new Map<string, Antenna[]>();
    for (const a of antennas) {
      const g = a.network || 'Other';
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)!.push(a);
    }
    const ordered = [...groups.entries()].sort(([a], [b]) => {
      const ia = GROUP_ORDER.indexOf(a);
      const ib = GROUP_ORDER.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return a.localeCompare(b);
    });
    return ordered.map(([group, list]) => ({
      group,
      antennas: list.map((a) => {
        const blocks = byAntenna.get(a.id) ?? [];
        const leoMinutes = blocks.filter((b) => !b.background).reduce((s, b) => s + b.durationMin, 0);
        return {
          antenna: a,
          blocks,
          utilisation: (leoMinutes / (span / 60_000)) * 100,
          conflicts: conflictsByAntenna.get(a.id) ?? [],
        };
      }),
    }));
  }, [dataset, results, timeframe, activeAntennaIds, visibleAntennaIds, span]);

  const ticks = useMemo(() => {
    const step = tickStepMs(timeframe.hours);
    const first = Math.ceil(timeframe.startMs / step) * step;
    const out: number[] = [];
    for (let t = first; t < timeframe.endMs; t += step) out.push(t);
    return out;
  }, [timeframe]);

  if (!results) return <div className="p-4 text-xs text-ink-3">Run the analysis to see the timeline.</div>;

  const missionName = (id: string) => dataset?.missions.find((m) => m.id === id)?.shortName ?? id;
  const nowX = xOf(now);
  const step = tickStepMs(timeframe.hours);
  const clampToWindow = (ms: number) => Math.max(timeframe.startMs, Math.min(timeframe.endMs, ms));

  return (
    <div className="relative flex h-full flex-col text-data" onMouseLeave={() => setHover(null)}>
      {/* Axis */}
      <div className="sticky top-0 z-10 flex h-7 shrink-0 border-b border-hairline bg-raised">
        <div className="w-40 shrink-0 border-r border-hairline px-2 py-1.5 text-[10px] uppercase tracking-wider text-ink-3">
          Antenna
        </div>
        <div
          role="slider"
          tabIndex={0}
          aria-label="Timeline clock"
          aria-valuemin={timeframe.startMs}
          aria-valuemax={timeframe.endMs}
          aria-valuenow={Math.round(clampToWindow(now))}
          aria-valuetext={fmtUtc(now)}
          className="relative flex-1 cursor-crosshair focus-visible:outline-offset-[-2px]"
          title="Click, or use the arrow keys, to move the clock"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const frac = (e.clientX - rect.left) / rect.width;
            setSimTime(timeframe.startMs + frac * span);
          }}
          onKeyDown={(e) => {
            let ms: number | null = null;
            if (e.key === 'ArrowRight' || e.key === 'ArrowUp') ms = now + step;
            else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') ms = now - step;
            else if (e.key === 'Home') ms = timeframe.startMs;
            else if (e.key === 'End') ms = timeframe.endMs;
            if (ms === null) return;
            e.preventDefault();
            setSimTime(clampToWindow(ms));
          }}
        >
          {ticks.map((t) => (
            <div
              key={t}
              className="absolute top-0 h-full border-l border-hairline pl-1 pt-1.5 font-mono text-[10px] text-ink-3"
              style={{ left: `${xOf(t)}%` }}
            >
              {fmtUtcShort(t, t - tickStepMs(timeframe.hours))}
            </div>
          ))}
          {nowX >= 0 && nowX <= 100 && (
            <div className="absolute top-0 h-full w-px bg-accent" style={{ left: `${nowX}%` }} />
          )}
        </div>
        <div className="w-14 shrink-0 border-l border-hairline px-2 py-1.5 text-right text-[10px] uppercase tracking-wider text-ink-3">
          Use
        </div>
      </div>

      <div className="min-h-0 flex-1">
        {rows.map((g) => (
          <div key={g.group}>
            <div className="flex h-6 items-center bg-inset/60 px-2 text-[10px] font-semibold uppercase tracking-wider text-ink-2">
              {g.group}
            </div>
            {g.antennas.map(({ antenna, blocks, utilisation, conflicts }) => {
              const selected = selection.antennaId === antenna.id;
              const high = utilisation > 70;
              return (
                <div
                  key={antenna.id}
                  data-testid="gantt-row"
                  onKeyDown={rowKeys}
                  className={cn('flex h-row border-b border-hairline/60', selected && 'bg-accent-soft/40')}
                >
                  <button
                    type="button"
                    data-row-item=""
                    aria-current={selected ? 'true' : undefined}
                    onClick={() => select({ antennaId: antenna.id, missionId: null, assetId: null })}
                    className={cn(
                      'w-40 shrink-0 truncate border-r border-hairline px-2 text-left hover:text-accent focus-visible:outline-offset-[-2px]',
                      selected ? 'font-semibold text-accent' : 'text-ink',
                    )}
                    title={antenna.name}
                  >
                    {antenna.shortName}
                    {antenna.capabilities.diameterM ? (
                      <span className="ml-1 text-ink-3">{antenna.capabilities.diameterM} m</span>
                    ) : null}
                  </button>
                  <div className="relative flex-1">
                    {blocks.map((b) => {
                      const left = Math.max(0, xOf(b.startMs));
                      const right = Math.min(100, xOf(b.endMs));
                      if (right <= 0 || left >= 100) return null;
                      const mine = selection.missionId === b.missionId;
                      return (
                        <button
                          key={b.key}
                          type="button"
                          tabIndex={-1}
                          data-row-item=""
                          aria-label={`${missionName(b.missionId)} ${fmtUtc(b.startMs)} ${b.durationMin} min`}
                          onClick={() => select({ missionId: b.missionId, antennaId: null, assetId: null })}
                          onMouseEnter={(e) =>
                            setHover({ block: b, antennaId: antenna.id, x: e.clientX, y: e.clientY })
                          }
                          onMouseMove={(e) =>
                            setHover({ block: b, antennaId: antenna.id, x: e.clientX, y: e.clientY })
                          }
                          className={cn(
                            'absolute rounded-[3px] transition-opacity',
                            b.background ? 'inset-y-0 opacity-15' : 'inset-y-1',
                            !b.background && selection.missionId && !mine && 'opacity-35',
                            mine && 'ring-1 ring-accent',
                          )}
                          style={{
                            left: `${left}%`,
                            width: `${Math.max(0.15, right - left)}%`,
                            background: b.color,
                          }}
                        />
                      );
                    })}
                    {conflicts.map((c, i) => {
                      const x = xOf(c.ms);
                      if (x < 0 || x > 100) return null;
                      return (
                        <div
                          key={i}
                          title={c.title}
                          aria-hidden
                          className="absolute top-0 h-1.5 w-1.5 -translate-x-1/2 rounded-b-sm bg-warning"
                          style={{ left: `${x}%` }}
                        />
                      );
                    })}
                    {nowX >= 0 && nowX <= 100 && (
                      <div
                        className="pointer-events-none absolute top-0 h-full w-px bg-accent/70"
                        style={{ left: `${nowX}%` }}
                      />
                    )}
                  </div>
                  <div
                    className={cn(
                      'flex w-14 shrink-0 items-center justify-end gap-1 border-l border-hairline px-2 font-mono',
                      high ? 'text-caution' : 'text-ink-2',
                    )}
                    title={high ? 'High utilisation (above 70%)' : undefined}
                  >
                    {high && <TriangleAlert aria-hidden className="h-3 w-3 shrink-0" />}
                    {pct(utilisation)}
                    {high && <span className="sr-only">, high</span>}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        {rows.length === 0 && <div className="p-4 text-ink-3">No antennas in the current filter.</div>}
      </div>

      {hover && (
        <div
          className="pointer-events-none fixed z-50 rounded-control border border-hairline bg-raised px-2.5 py-1.5 text-xs shadow-elev-2"
          style={{ left: hover.x + 12, top: hover.y - 44 }}
        >
          <div className="font-medium text-ink">{missionName(hover.block.missionId)}</div>
          <div className="font-mono text-ink-2">
            {fmtUtc(hover.block.startMs)} to {fmtUtcShort(hover.block.endMs, hover.block.startMs)}
          </div>
          <div className="text-ink-3">
            {fmtMinutes(hover.block.durationMin)}, max elevation {Math.round(hover.block.maxElevation)} deg
          </div>
        </div>
      )}
    </div>
  );
}
